import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import QRCode from "qrcode";
import type { MultiFactorResolver } from "firebase/auth";
import { completeTotp, devSignIn, passwordSignIn, sendPasswordSetup, startTotpEnrollment } from "../lib/auth";
import { useSession } from "../lib/session";

type Step =
  | { k: "creds" }
  | { k: "reset" }
  | { k: "totp"; resolver: MultiFactorResolver }
  | { k: "enroll"; qr: string; finish: (code: string) => Promise<void> };

export function LoginPage() {
  const { config, refreshMe } = useSession();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState(config.authMode === "dev" ? "admin@demo.local" : "");
  const [name, setName] = useState("Demo Admin");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<Step>({ k: "creds" });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await refreshMe();
    nav(next.startsWith("/") ? next : "/", { replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setBusy(true);
    try {
      if (step.k === "reset") {
        await sendPasswordSetup(email.trim());
        setInfo(`If ${email.trim()} has an account, a link to set your password is on its way. Open it, choose a password, then sign in here.`);
        setStep({ k: "creds" });
      } else if (config.authMode === "dev") {
        await devSignIn(email.trim(), name.trim() || email);
        await done();
      } else if (step.k === "creds") {
        const r = await passwordSignIn(email.trim(), password);
        if (r.kind === "done") await done();
        else if (r.kind === "verify") setInfo(`We've sent a verification link to ${r.email}. Open it, then sign in again.`);
        else if (r.kind === "mfa") setStep({ k: "totp", resolver: r.resolver });
        else {
          const en = await startTotpEnrollment(r.user);
          setStep({ k: "enroll", qr: await QRCode.toDataURL(en.uri, { margin: 1, width: 220 }), finish: en.finish });
        }
      } else if (step.k === "totp") {
        await completeTotp(step.resolver, code.trim());
        await done();
      } else {
        await step.finish(code.trim());
        await done();
      }
    } catch (err) {
      setError(step.k === "creds" || step.k === "reset" ? (err as Error).message : "That code didn't work. Check the time on your phone and try the newest code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="art">
        <div className="stack" style={{ gap: 14 }}>
          <img src="/brand/gramin-kriti-logo-light.svg" width={200} alt="Gramin Kriti Technologies" style={{ height: "auto" }} />
          <b style={{ fontSize: 17, color: "#fff" }}>Kriti Voice</b>
        </div>
        <div>
          <h1>Hear how the work really happens.</h1>
          <p>Discovery interviews in any language, turned into verified evidence for process maps, root causes and change.</p>
        </div>
        <p className="small" style={{ color: "#8fae9f" }}>Every conversation is consented, encrypted and kept in the client's own space.</p>
        <svg className="rows" width="520" height="320" viewBox="0 0 520 320" aria-hidden>
          {Array.from({ length: 9 }, (_, i) => (
            <path key={i} d={`M0 ${40 + i * 32} C 140 ${20 + i * 32}, 300 ${70 + i * 32}, 520 ${30 + i * 32}`} stroke="#E2B65C" strokeWidth="2" fill="none" />
          ))}
        </svg>
      </div>
      <form onSubmit={submit}>
        <div>
          <h2>{step.k === "creds" ? "Sign in" : step.k === "reset" ? "Set or reset your password" : step.k === "totp" ? "Two-step verification" : "Set up two-step verification"}</h2>
          {config.authMode === "dev" && <p className="muted small" style={{ marginTop: 6 }}>Development sign-in. Production uses verified accounts with two-step verification.</p>}
        </div>
        {step.k === "creds" && (
          <>
            <label className="field">
              <span>Work email</span>
              <input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            {config.authMode === "dev" ? (
              <label className="field">
                <span>Your name</span>
                <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
            ) : (
              <label className="field">
                <span>Password</span>
                <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </label>
            )}
          </>
        )}
        {step.k === "reset" && (
          <label className="field">
            <span>Work email</span>
            <input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </label>
        )}
        {step.k === "enroll" && (
          <div className="stack tight">
            <p className="muted small">Scan this with an authenticator app (Google Authenticator, Microsoft Authenticator), then enter the 6-digit code it shows.</p>
            <img src={step.qr} width={200} height={200} alt="Authenticator setup code" style={{ borderRadius: 8 }} />
          </div>
        )}
        {(step.k === "totp" || step.k === "enroll") && (
          <label className="field">
            <span>6-digit code</span>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
          </label>
        )}
        {error && <div className="error-box" role="alert">{error}</div>}
        {info && (
          <div className="note-box" role="status">
            {info}
          </div>
        )}
        <button className="btn primary big" disabled={busy}>
          {busy ? "Please wait…" : step.k === "creds" ? "Sign in" : step.k === "reset" ? "Email me a link" : "Verify"}
        </button>
        {config.authMode !== "dev" && (step.k === "creds" || step.k === "reset") && (
          <button
            type="button"
            className="btn ghost small"
            onClick={() => {
              setError(null);
              setInfo(null);
              setStep(step.k === "creds" ? { k: "reset" } : { k: "creds" });
            }}
          >
            {step.k === "creds" ? "First time, or forgot your password? Set it here" : "Back to sign in"}
          </button>
        )}
      </form>
    </div>
  );
}
